<template>
	<div
		v-if="display !== 'none'"
		:id="!isExpandable ? null : id"
		class="cog-page-progress"
		:class="[{ 'cog-page-progress--compact': isNarrow, 'cog-page-progress--at-end': onLastPage }, 'cog-page-progress--' + display]"
		:aria-expanded="!isExpandable ? null : verticalBarsExpanded ? 'true' : 'false'"
		:data-backwards="backwards"
	>
		<el-scrollbar tag="ol">
			<li
				v-for="(page, index) in pages"
				:key="index"
				class="cog-page-progress__page"
				:class="[{ 'is-active': page === currentPage, 'is-disabled': cantGoBack(index), 'cog-page-progress__page--no-title': !page.title }, scrollingStepsShiftClass]"
				:data-title="page.title"
			>
				<a
					:href="cantGoBack(page.number) ? false : ''"
					:title="isNarrow ? page.title : false"
					:data-page-number="index + 1"
					@click.prevent="goto(page.number, $event)"
					@keydown.prevent.space="goto(page.number)"
				>
					<span v-if="showTitles" class="cog-page-progress__page-title">{{ page.title | preventSingleCharWidow }}</span>
				</a>
			</li>
		</el-scrollbar>
		<div v-if="isExpandable" class="cog-wrapper">
			<button
				class="cog-page-progress__expand-button"
				:title="verticalBarsExpanded ? $resource('collapse') : $resource('expand')"
				:aria-controls="id"
				@click.prevent="handleCollapse()"
			>
				<i-chevron />
			</button>
		</div>
	</div>
</template>

<script>
	import 'src/style/_scrollbar.scss';

	import scrollIntoView from 'scroll-into-view';
	import IChevron from './../assets/chevron.svg';
	import Vue from 'vue';
	import { Scrollbar } from '@cognitoforms/element-ui';

	Vue.use(Scrollbar);

	export default {
		name: 'CPageProgress',
		components: {
			'el-scrollbar': Scrollbar,
			IChevron
		},
		filters: {
			preventSingleCharWidow(value) {
				/*
					In the 'bar' display, page titles are allowed to go to multiple lines and...
					"Page
					1"
					looks real dumb.
				*/
				if (!/ \S$/.test(value)) return value;
				else {
					const spaceIndex = value.lastIndexOf(' ');
					return value.substr(0, spaceIndex) + '\xa0' + value.substr(spaceIndex + 1);
				}
			}
		},
		inject: ['$resource'],
		props: {
			pages: {
				type: Array,
				default: () => []
			},
			currentPage: { type: Object, required: true },
			showTitles: Boolean,
			display: {
				type: String,
				validator: value => {
					return value === 'steps' || value === 'bar' || value === 'none';
				},
				default: 'steps'
			},
			formId: { type: Number, required: true }
		},
		data() {
			return {
				isNarrow: false,
				backwards: false,
				width: null,
				resizeObserver: null,
				verticalBarsExpanded: true,
				verticalBarsWholeHeight: null,
				verticalBarsActivePageHeight: null,
				scrollingStepsShift: null,
				notNarrowWidth: null,
				narrowWidth: null,
				onLastPage: false
			};
		},
		computed: {
			isExpandable() {
				return this.isNarrow && this.display === 'bar' && this.pages.length > 7;
			},
			scrollingStepsShiftClass() {
				return this.scrollingStepsShift ? this.scrollingStepsShift === 'expand' ? 'is-expanded' : 'is-compact' : null;
			},
			id() {
				return 'cog-page-progress--' + this.formId;
			}
		},
		watch: {
			currentPage(newPage, oldPage) {
				this.backwards = oldPage.number > newPage.number;
				this.onLastPage = newPage.number === this.pages[this.pages.length - 1].number;
				if (this.verticalBarsExpanded)
					this.handleCollapse();
				if (this.isExpandable && !this.verticalBarsExpanded)
					this.$nextTick(() => {
						this.setHeight();
					});
				else if (!this.isExpandable)
					this.centerPager();
			},
			pages() {
				this.notNarrowWidth = null;
				this.narrowWidth = null;
			}
		},
		mounted() {
			this.resizeObserver = new ResizeObserver(() => this.handleResize());
			this.resizeObserver.observe(this.$el);
		},
		destroyed() {
			this.resizeObserver.unobserve(this.$el);
		},
		methods: {
			cantGoBack(pageNumber) {
				return this.pages.slice(pageNumber + 1, this.pages.indexOf(this.currentPage) + 1).some(p => !p.backButton);
			},
			goto(pageNumber) {
				this.$emit('goto', pageNumber);
			},
			handleResize() {
				if (this.verticalBarsExpanded) {
					this.clearHeight();
					this.setHeight();
				}

				const pagerWidth = this.$el.clientWidth;
				if (this.width === pagerWidth || this.display === 'none')
					return;

				if (this.display === 'steps' && pagerWidth > 550) {
					this.isNarrow = false;
					return;
				}

				if (this.notNarrowWidth && this.notNarrowWidth < pagerWidth) {
					this.isNarrow = false;
					return;
				}

				if (this.narrowWidth && this.narrowWidth > pagerWidth) {
					this.isNarrow = true;
					this.$nextTick(()=> {
						this.shiftStepsPages(pagerWidth);
					});
					return;
				}

				const ol = this.$el.querySelector('ol');

				// Temporarily hide the component and remove compact style so things can be remeasured.
				// This is why the wrapper div around this component is necessary.
				this.$el.style.height = this.$el.clientHeight + 'px';
				this.$el.style.overflow = 'hidden';
				this.$el.style.opacity = 0;

				// This is needed for 'bar' version.
				ol.style.marginBottom = getComputedStyle(this.$el).marginBottom;

				this.isNarrow = false;

				setTimeout(() => {
					// If 'steps' view and a sufficently narrow form, convert to compact mode if a page title goes to the next line.

					// Otherwise, if 'bar' view, first measure to see if the end of the last page title is beyond the end of the pager container.
					// Also make sure no one page title is a different width than the others. If it is, convert to compact mode.
					// Because they are flex items, they will be the same width if there is enough room.
					// Also, if one page title goes to 3 lines, that's too much, convert to compact.
					if (this.display === 'steps') {
						const computedStyle = getComputedStyle(ol);
						const height = ol.clientHeight - parseInt(computedStyle.paddingBottom, 10) - parseInt(computedStyle.paddingTop, 10);
						const itemHeight = this.$el.querySelector('li').clientHeight;
						// Assuming one line, the li should be the same height of the ol, give or take one pixel to allow for page zoom oddity: bug 15763
						this.isNarrow = !isNaN(height) && !isNaN(itemHeight) && Math.abs(height - itemHeight) > 1;

						// Check if pages are awkwardly cropped by edge of viewport
						this.$nextTick(()=> {
							this.shiftStepsPages(pagerWidth);
						});
					}
					else if (this.display === 'bar') {
						const items = ol.querySelectorAll('li');
						const pageListStyles = getComputedStyle(ol);
						const pagerEnd = ol.getBoundingClientRect().right - parseInt(pageListStyles.paddingLeft, 10);

						if (pagerEnd < items[items.length - 1].getBoundingClientRect().right - 1) {
							this.isNarrow = true;
						}
						else {
							const lineHeight = parseInt(getComputedStyle(items[0]).lineHeight, 10);
							for (let i = 0; i < items.length; i++) {
								const prevWidth = i ? items[i - 1].clientWidth : null;
								const width = items[i].clientWidth;
								const height = items[i].clientHeight;
								const paddingEstimate = 30;
								if (
									(prevWidth && prevWidth < width - 1) ||
									prevWidth > width + 1 ||
									height > lineHeight * 3 + paddingEstimate
								) {
									this.isNarrow = true;
									break;
								}
							}
						}
					}

					// Remove temporary inline styles applied for remeasure
					this.$el.style.overflow = null;
					this.$el.style.opacity = null;
					this.$el.style.height = null;
					ol.style.marginBottom = null;

					this.width = this.$el.clientWidth;

					if (this.isNarrow)
						this.narrowWidth = this.width;
					else
						this.notNarrowWidth = this.width;

					// Set height
					this.$nextTick(() => {
						this.setHeight();
					});
				});
			},
			shiftStepsPages(pagerWidth) {
				// Remove any preexisting modifications
				this.scrollingStepsShift = null;

				this.$nextTick(()=> {
					const lis = this.$el.querySelectorAll('li');
					let rightEdgeOfTitleNearScreenEdge;
					let difference;
					const maxDifference = 20;
					const minDifference = -15;
					const differenceBreakpoint = 5;
					for (const li of lis) {
						rightEdgeOfTitleNearScreenEdge = Math.round(li.getBoundingClientRect().right);
						difference = rightEdgeOfTitleNearScreenEdge - pagerWidth;
						if (difference > minDifference && difference < maxDifference) {
							this.scrollingStepsShift = difference < differenceBreakpoint ? 'contract' : 'expand';
							return;
						}
						else if (rightEdgeOfTitleNearScreenEdge > pagerWidth + maxDifference)
							return;
					}
				});
			},
			centerPager() {
				if (!this.isNarrow) return;

				setTimeout(() => {
					scrollIntoView(this.$el.querySelector('.is-active a'), { time: 500 });
				});
			},
			handleCollapse() {
				this.verticalBarsExpanded = !this.verticalBarsExpanded;
				this.setHeight();
			},
			setHeight() {
				if (!this.verticalBarsWholeHeight)
					this.verticalBarsWholeHeight = this.$el.querySelector('.el-scrollbar__view').getBoundingClientRect().height;
				const container = this.$el.querySelector('.el-scrollbar__wrap');
				if (this.isExpandable) {
					const height = this.verticalBarsExpanded ? this.verticalBarsWholeHeight : container.querySelector('.is-active').clientHeight + 70;
					container.style.maxHeight = height + 'px';
					this.centerPager();

					// If going from expanded to collapsed it needs to re-center after the collapse is finished
					if (!this.verticalBarsExpanded)
						container.addEventListener('transitionend', () => {
							this.centerPager();
						}, { once: true });
				}
				else {
					this.clearHeight();
				}
			},
			clearHeight() {
				this.$el.querySelector('.el-scrollbar__wrap').style.maxHeight = '';
				this.verticalBarsWholeHeight = null;
			}
		}
	};
</script>

<style lang="scss">
// 1. General page-progress
//  a. compact
//		- general
// 	  - compact steps
// 		- compact bar
// 	b. steps
// 	c. bar
// 2. Non-Chameleon specific styles
// 3. Chameleon styles
#{$specificity-base} {

	.cog-page-progress {

		.el-scrollbar__bar.is-vertical {
			display: none;
		}

		ol {
			width: 100%;
			margin: 0;
			padding: var(--gutter) var(--form__margins--responsive) $gutter-quarter;
		}

		li {
			padding-top: $gutter-sixth;
			padding-bottom: $gutter-sixth;
		}

		li.is-disabled {
			cursor: not-allowed;
			opacity: .7;
		}

		li.is-disabled a {
			pointer-events: none;

			&::before {
				background:
 repeating-linear-gradient(
					-45deg,
					hsla(var(--background-hsl), .8) 0,
					hsla(var(--background-hsl), .8) 2px,
					transparent 2px,
					transparent 4px
				);
				border: 0;
			}
		}

		&__expand-button {
			display: block;
			width: 100%;
			background: transparent;
			border: 0;
			border-top: 1px solid hsla(var(--background-hsl), .5);
			padding: $gutter-quarter;
			padding-right: 0;
			color: var(--color);
			font-size: var(--small-text);
			text-align: right;
		}

		&[aria-expanded='true'] .cog-icon {
			transform: rotate(180deg);
		}

		&--compact {

			&.cog-page-progress--steps {
				$pagerTitleContainerWidth: 12.5em;

				ol {
					display: flex;
					flex-wrap: nowrap;
				}

				li:not(:last-child) {
					// must be at least 8px for mobile devices
					padding-right: 10px;

					// On mobile - shift the page titles to avoid the appearance that you can't scroll horizontally
					&.is-expanded {
						padding-right: 15px;

						span {
							letter-spacing: .02em;
						}
					}

					&.is-compact {
						padding-right: 8px;

						span {
							margin-left: $gutter-quarter;
							letter-spacing: -.01em;
						}
					}
				}

				a,
				.cog-page-progress__page-title {
					white-space: nowrap;
				}

				li + li.is-active:not(:last-child)::before {
					margin-left: calc(math.div(-$pagerTitleContainerWidth, 2) + 1.5em);
				}

				.is-active::before {
					bottom: calc(100% - 1em);
					width: $pagerTitleContainerWidth;
				}
			}

			&.cog-page-progress--bar {

				.el-scrollbar {
					padding-bottom: $gutter-half;
				}

				.el-scrollbar__wrap {
					max-height: none;
					transition: max-height $speed-half;
					overflow-x: hidden;
				}

				&[aria-expanded='false'] .el-scrollbar {
					-webkit-mask-image: linear-gradient(to bottom, transparent 0, black 30px, black calc(100% - 30px), transparent 100%);
					mask-image: linear-gradient(to bottom, transparent 0, black 30px, black calc(100% - 30px), transparent 100%);
				}

				&.cog-page-progress--at-end[aria-expanded='false'] .el-scrollbar {
					-webkit-mask-image: linear-gradient(to bottom, transparent 0, black 30px, black 100%);
					mask-image: linear-gradient(to bottom, transparent 0, black 30px, black 100%);
				}

				ol {
					display: block;
				}

				a {
					border-left-color: hsla(var(--background-hsl), .5);
					padding-left: .75em;
				}

				a::before {
					bottom: 0;
					top: initial;
					width: 3px;
					min-height: 0%;
					max-height: 0;
					opacity: 0;
					transition: min-height var(--speed), opacity var(--speed);

					// override non-compact data-backwards rule
					@include at-root(1) {

						.cog-page-progress--compact.cog-page-progress--bar a::before {
							left: -3px;
						}
					}
				}

				.is-active a::before {
					bottom: 100%;
					top: 0;
					min-height: 100%;
					opacity: 1;
				}

				&[data-backwards='true'] {

					a::before {
						bottom: 100%;
						top: 0%;
					}

					.is-active ::before {
						bottom: 0%;
						top: initial;
					}
				}

				a::after {
					display: none;
				}

				a[href] {
					border-top: 0;
					border-left-style: solid;
				}

				.cog-page-progress__page--no-title.is-active a:focus::before {
					transform: scaleY(1) scaleX(1.5);
				}

				a:focus {
					border-left-color: hsla(var(--background-hsl), 1);
				}
			}
		}

		&--steps {

			li {
				display: inline-block;
			}

			a {
				display: inline-block;
				margin-left: -3px;
				padding: 3px;

				span {
					margin-right: $gutter-half;
				}
			}

			a > span,
			a::before {
				vertical-align: middle;
			}

			a::before {
				// inline-flex because NVDA has a bug(?) that prevents keyboard pressing of these buttons when set to inline-block.
				display: inline-flex;
				align-items: center;
				justify-content: center;
				width: calc(#{$gutter-quarter} + 1em);
				height: calc(#{$gutter-quarter} + 1em);
				background: hsla(var(--background-hsl), .6);
				border-width: var(--border-width);
				border-style: solid;
				border-color: transparent;
				border-radius: 50%;
				content: attr(data-page-number);

				// Can't use .75ems here because of bug 18927
				// IE seems to compound two .75ems
				font-size: calc(var(--font-size) * .75);
				transition: transform $speed-quarter;

				@media print {
					border-color: var(--input__border-color);
				}
			}

			a:focus::before {
				border-color: var(--highlight);
				box-shadow: inset 0 0 0 calc(var(--border-width) + .5px) var(--highlight);
			}

			.is-active ::before {
				background: var(--highlight-reverse);
				border-color: var(--highlight);
				color: var(--highlight);
				font-weight: bold;
			}
		}

		&--bar {

			ol,
			li {
				display: flex;
				width: 100%;
			}

			li {
				align-items: flex-start;
			}

			li:not(:last-child) {
				// must be at least 8px for mobile devices
				margin-right: 10px;
			}

			a {
				display: block;
				position: relative;
				width: 100%;
				border-top-width: calc(var(--input__border-width) * 3);
				border-top-style: dotted; // disabled
				border-top-color: hsla(var(--background-hsl), .5);
				padding-top: $gutter-quarter;
				padding-bottom: $gutter-quarter;

				// not disabled
				&[href] {
					border-top-style: solid;
				}

				&::before {
					display: block;
					position: absolute;
					right: 0;
					top: calc(-3 * var(--input__border-width));
					width: 0;
					height: calc(var(--input__border-width) * 3);
					background: var(--highlight);
					content: '';
					transition: width var(--speed);
				}

				&::after {
					// Increase clickable area
					position: absolute;
					left: 0;
					top: -30px;
					width: 100%;
					height: 30px;
					content: '';
				}
			}

			a:focus {
				border-top-color: hsla(var(--background-hsl), 1);

				span {
					border-bottom: var(--input__border-width) solid var(--highlight);
				}
			}

			.cog-page-progress__page--no-title.is-active a:focus::before {
				transform: scaleY(1.5);
			}

			.is-active ::before {
				left: 0;
				width: 100%;
			}

			&[data-backwards='true'] {

				li ::before {
					left: 0;
				}

				.is-active ::before {
					left: initial;
					right: 0;
				}
			}
		}
	}

	.cog-cognito--styled .cog-page-progress {

		a {
			color: var(--color);
			text-decoration: none;
		}

		li.is-active a {
			cursor: default;
		}

		a span {
			padding: 2px 0;
		}

		&--steps .cog-page-progress__page-title {
			margin-left: .5em;
		}
	}

	.cog-cognito--chameleon .cog-page-progress a::before {
		content: none;
	}
}
</style>